import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_BASE_URL =
  typeof window !== 'undefined'
    ? '/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

// Configuration d'axios pour l'authentification
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token d'authentification
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // éviter les redirections agressives ici; laisser l'app gérer
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Vérifier si le token est encore valide (POST côté backend)
          const response = await authApi.post('/auth/verify');
          if (response.data && response.data.user) {
            setUser(response.data.user);
            setToken(storedToken);
            setIsAuthenticated(true);
          } else {
            throw new Error('Format de réponse invalide');
          }
        } catch (error) {
          // Token invalide ou expiré, nettoyer sans spammer la console (401 est un cas attendu).
          const status = error.response?.status;
          if (status !== 401 && process.env.NODE_ENV === 'development') {
            // Garder un avertissement discret pour les erreurs non-attendues.
            console.warn('[Auth] Vérification échouée (non-401):', error.response?.data || error.message, 'URL=', API_BASE_URL);
          }
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setToken(null);
          setIsAuthenticated(false);
        }
      } else {
        // Pas de token stocké, utilisateur non authentifié
        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Fonction de connexion
  const login = async (identifier, password) => {
    try {
      const response = await authApi.post('/auth/login', { identifier, password });

      if (!response.data || !response.data.token || !response.data.user) {
        throw new Error('Format de réponse invalide du serveur');
      }

      const { token: newToken, user: userData } = response.data;

      // Stocker les données d'authentification
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setToken(newToken);
      setIsAuthenticated(true);

      return response.data;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      if (process.env.NODE_ENV === 'development') {
        console.error('URL API:', API_BASE_URL);
        console.error('Identifiant utilisé:', identifier);
        console.error('Détails erreur:', error.response || error.message);
      }
      throw error;
    }
  };

  // Fonction d'inscription
  const register = async (username, email, password, role = 'editor') => {
    try {
      const response = await authApi.post('/auth/register', {
        username,
        email,
        password,
        role
      });

      const { token: newToken, user: userData } = response.data;

      // Stocker les données d'authentification
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      setToken(newToken);
      setIsAuthenticated(true);

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  // Fonction pour mettre à jour le profil utilisateur
  const updateProfile = async (userData) => {
    try {
      const response = await authApi.put('/auth/profile', userData);
      const updatedUser = response.data.user;

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Fonction pour changer le mot de passe
  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await authApi.put('/auth/change-password', {
        currentPassword,
        newPassword
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Fonction pour réinitialiser le mot de passe
  const resetPassword = async (email) => {
    try {
      const response = await authApi.post('/auth/reset-password', {
        email
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Fonction pour vérifier les permissions
  const hasRole = (requiredRole) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.role === requiredRole;
  };

  // Fonction pour vérifier si l'utilisateur est admin
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Fonction pour vérifier si l'utilisateur est éditeur
  const isEditor = () => {
    return user?.role === 'editor' || user?.role === 'admin';
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    token,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    resetPassword,
    hasRole,
    isAdmin,
    isEditor
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export default AuthContext;
